import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { toast } from "sonner";
import { Stethoscope, ShieldCheck, CheckCircle2, AlertCircle, ArrowRight, Lock } from "lucide-react";

const COPY = {
  en: {
    label: "Find Care",
    title: "Find an orthopedic or spine doctor who takes your insurance.",
    subtitle:
      "Tell us how you're hurting and which insurance you have. We'll match you with a trusted orthopedic or spine provider in New York or New Jersey — usually within one business day.",
    pill_specialties: "Orthopedic · Spine",
    pill_states: "NY · NJ providers",
    pill_spanish: "Hablamos español",
    privacy_title: "Your privacy is protected",
    privacy_body:
      "This form is encrypted in transit and stored separately from any legal-case data. We only ask for your insurance carrier name — never your member ID or policy number. We never sell your information. Your details are used solely to match you with care.",
    consent_label: "I understand my health information will be shared only with the provider matching me to care.",
    section_about_you: "About you",
    section_symptoms: "What are you feeling?",
    section_insurance: "Your insurance",
    section_finish: "Almost done",
    name: "Full name",
    email: "Email",
    phone: "Phone",
    phone_ph: "(555) 555-1234",
    state: "State",
    city: "City (optional)",
    city_ph: "e.g. Brooklyn, Newark",
    body_area: "Main body area (optional)",
    body_area_ph: "e.g. lower back, right knee",
    symptoms_helper: "Check all that apply",
    pain_level: "Pain level",
    pain_low: "Mild",
    pain_high: "Severe",
    duration: "How long?",
    duration_acute: "Less than 4 weeks (acute)",
    duration_subacute: "4–12 weeks (subacute)",
    duration_chronic: "More than 3 months (chronic)",
    accident_related: "This pain is related to an accident or injury",
    insurance_label: "Insurance carrier",
    insurance_helper: "We only ask for the carrier name — never your member ID.",
    insurance_pick: "Select your carrier…",
    notes_label: "Anything else we should know? (optional)",
    notes_ph: "Past treatments, allergies, language preference, scheduling needs…",
    submit: "Find me a doctor",
    submitting: "Submitting…",
    success_title: "Got it. We're matching you now.",
    success_body:
      "A care coordinator will reach out within 1 business day with a matched orthopedic or spine provider. If you're in pain right now or need urgent care, please call 911 or visit the nearest ER.",
    success_home: "Back to Home",
    success_call: "Call our hotline",
    err_consent: "Please accept the privacy notice to continue.",
    err_required: "Please fill in your name, email, phone, and pick your insurance carrier.",
    toast_success: "Care request submitted — we'll be in touch within 1 business day.",
  },
  es: {
    label: "Encuentra Atención",
    title: "Encuentra un médico ortopédico o de columna que acepte tu seguro.",
    subtitle:
      "Cuéntanos cómo te sientes y qué seguro tienes. Te conectaremos con un proveedor ortopédico o de columna de confianza en Nueva York o Nueva Jersey — normalmente en 1 día hábil.",
    pill_specialties: "Ortopedia · Columna",
    pill_states: "Proveedores en NY · NJ",
    pill_spanish: "Hablamos español",
    privacy_title: "Tu privacidad está protegida",
    privacy_body:
      "Este formulario está cifrado en tránsito y se guarda separado de cualquier dato de caso legal. Solo pedimos el nombre de tu compañía de seguro — nunca tu número de miembro o póliza. Nunca vendemos tu información. Tus datos se usan únicamente para conectarte con atención médica.",
    consent_label: "Entiendo que mi información de salud se compartirá solo con el proveedor que me atienda.",
    section_about_you: "Sobre ti",
    section_symptoms: "¿Cómo te sientes?",
    section_insurance: "Tu seguro",
    section_finish: "Casi terminamos",
    name: "Nombre completo",
    email: "Correo electrónico",
    phone: "Teléfono",
    phone_ph: "(555) 555-1234",
    state: "Estado",
    city: "Ciudad (opcional)",
    city_ph: "ej. Brooklyn, Newark",
    body_area: "Área principal del cuerpo (opcional)",
    body_area_ph: "ej. espalda baja, rodilla derecha",
    symptoms_helper: "Marca todo lo que apliquen",
    pain_level: "Nivel de dolor",
    pain_low: "Leve",
    pain_high: "Severo",
    duration: "¿Cuánto tiempo?",
    duration_acute: "Menos de 4 semanas (agudo)",
    duration_subacute: "4–12 semanas (subagudo)",
    duration_chronic: "Más de 3 meses (crónico)",
    accident_related: "Este dolor está relacionado con un accidente o lesión",
    insurance_label: "Compañía de seguro",
    insurance_helper: "Solo pedimos el nombre de la compañía — nunca tu número de miembro.",
    insurance_pick: "Selecciona tu seguro…",
    notes_label: "¿Algo más que debamos saber? (opcional)",
    notes_ph: "Tratamientos pasados, alergias, idioma preferido, horarios…",
    submit: "Encuéntrame un médico",
    submitting: "Enviando…",
    success_title: "Recibido. Estamos buscando tu proveedor.",
    success_body:
      "Un coordinador de atención te contactará en 1 día hábil con un proveedor ortopédico o de columna. Si tienes dolor severo ahora o necesitas atención urgente, llama al 911 o ve a la sala de emergencias más cercana.",
    success_home: "Volver al Inicio",
    success_call: "Llamar a nuestra línea",
    err_consent: "Por favor acepta el aviso de privacidad para continuar.",
    err_required: "Por favor llena tu nombre, correo, teléfono y selecciona tu seguro.",
    toast_success: "Solicitud enviada — te contactaremos en 1 día hábil.",
  },
};

// Localized symptom display labels mapped from the backend's canonical English labels
const SYMPTOM_ES = {
  "Lower back pain": "Dolor de espalda baja",
  "Upper / mid back pain": "Dolor de espalda media/alta",
  "Neck pain": "Dolor de cuello",
  "Shoulder pain": "Dolor de hombro",
  "Knee pain": "Dolor de rodilla",
  "Hip pain": "Dolor de cadera",
  "Elbow pain": "Dolor de codo",
  "Wrist / hand pain": "Dolor de muñeca / mano",
  "Ankle / foot pain": "Dolor de tobillo / pie",
  "Numbness or tingling": "Entumecimiento u hormigueo",
  "Muscle weakness": "Debilidad muscular",
  "Reduced range of motion": "Rango de movimiento limitado",
  "Joint stiffness": "Rigidez articular",
  "Pain radiating down arm": "Dolor irradiado al brazo",
  "Sciatica (pain down leg)": "Ciática (dolor en la pierna)",
  "Post-injury headaches": "Dolores de cabeza tras lesión",
  "Recent injury (under 6 weeks)": "Lesión reciente (menos de 6 semanas)",
  "Chronic pain (over 3 months)": "Dolor crónico (más de 3 meses)",
};

function labelSymptom(canonical, lang) {
  return lang === "es" ? (SYMPTOM_ES[canonical] || canonical) : canonical;
}

export default function CareMatch() {
  const navigate = useNavigate();
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.en;

  const [symptoms, setSymptoms] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [optsLoading, setOptsLoading] = useState(true);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    state: "NY",
    city: "",
    symptoms: [],
    body_area: "",
    pain_level: 5,
    duration: "subacute",
    accident_related: false,
    insurance_carrier: "",
    notes: "",
    consent_given: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/care/options")
      .then((r) => {
        setSymptoms(r.data?.symptoms || []);
        setCarriers(r.data?.insurance_carriers || []);
      })
      .catch(() => {})
      .finally(() => setOptsLoading(false));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSymptom = (s) =>
    setForm((f) => ({
      ...f,
      symptoms: f.symptoms.includes(s) ? f.symptoms.filter((x) => x !== s) : [...f.symptoms, s],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_given) return toast.error(c.err_consent);
    if (!form.full_name.trim() || !form.email.trim() || !form.phone.trim() || !form.insurance_carrier) {
      return toast.error(c.err_required);
    }
    setSubmitting(true);
    try {
      await api.post("/care-requests", { ...form, language: lang });
      setDone(true);
      toast.success(c.toast_success);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <section className="mx-auto max-w-3xl px-6 py-24 lg:py-32" data-testid="care-success">
          <div className="card-soft p-10 text-center">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-full" style={{ background: "#E6F6ED" }}>
              <CheckCircle2 className="h-7 w-7 text-[#2D6A4F]" />
            </div>
            <h1 className="mt-6 font-display text-3xl font-black tracking-tight text-[#11322A] sm:text-4xl">
              {c.success_title}
            </h1>
            <p className="mt-4 text-[#5C6B68]">{c.success_body}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <a href="tel:+18005551234" className="btn-primary" data-testid="care-success-call">{c.success_call}</a>
              <button className="btn-secondary" onClick={() => navigate("/")} data-testid="care-success-home">{c.success_home}</button>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="care-match-page">
      <Navbar />
      <section className="mx-auto max-w-4xl px-6 py-16 lg:py-20">
        <span className="label-cap">{c.label}</span>
        <h1 className="mt-3 font-display text-3xl font-black tracking-tight text-[#11322A] sm:text-4xl">
          {c.title}
        </h1>
        <p className="mt-3 max-w-2xl text-[#5C6B68]">{c.subtitle}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="pill"><Stethoscope className="h-3 w-3" /> {c.pill_specialties}</span>
          <span className="pill">{c.pill_states}</span>
          <span className="pill" style={{ color: "#1d4ed8", borderColor: "#b3d4ff", background: "#E6F3FF" }}>
            {c.pill_spanish}
          </span>
        </div>

        {/* Privacy notice */}
        <div className="mt-6 flex items-start gap-3 rounded-2xl border border-[#bce5cc] bg-[#E6F6ED] p-5" data-testid="care-privacy">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white text-[#166534]" style={{ border: "1px solid #bce5cc" }}>
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-sm font-bold text-[#166534]">{c.privacy_title}</div>
            <p className="mt-1 text-sm text-[#0B1317]">{c.privacy_body}</p>
          </div>
        </div>

        <form onSubmit={submit} className="mt-8 card-soft p-6 sm:p-10" data-testid="care-form">
          {/* About you */}
          <div className="label-cap">{c.section_about_you}</div>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label-cap text-[10px]">{c.name}</label>
              <input className="input-field mt-1" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} required data-testid="care-name" />
            </div>
            <div>
              <label className="label-cap text-[10px]">{c.email}</label>
              <input type="email" className="input-field mt-1" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="care-email" />
            </div>
            <div>
              <label className="label-cap text-[10px]">{c.phone}</label>
              <input type="tel" className="input-field mt-1" placeholder={c.phone_ph} value={form.phone} onChange={(e) => set("phone", e.target.value)} required data-testid="care-phone" />
            </div>
            <div>
              <label className="label-cap text-[10px]">{c.state}</label>
              <div className="mt-1 flex gap-2">
                {["NY", "NJ"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => set("state", s)}
                    className={`flex-1 rounded-lg border-2 py-2 font-display text-sm font-bold transition ${form.state === s ? "border-[#11322A] bg-[#11322A] text-white" : "border-[#E0E4E2] text-[#0B1317] hover:border-[#11322A]"}`}
                    data-testid={`care-state-${s}`}
                  >
                    {s === "NY" ? (lang === "es" ? "Nueva York" : "New York") : (lang === "es" ? "Nueva Jersey" : "New Jersey")}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="label-cap text-[10px]">{c.city}</label>
              <input className="input-field mt-1" placeholder={c.city_ph} value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="care-city" />
            </div>
          </div>

          {/* Symptoms */}
          <div className="mt-8 label-cap">{c.section_symptoms}</div>
          <p className="mt-1 text-xs text-[#5C6B68]">{c.symptoms_helper}</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="care-symptoms">
            {(optsLoading ? [] : symptoms).map((s) => {
              const active = form.symptoms.includes(s);
              return (
                <button
                  type="button"
                  key={s}
                  onClick={() => toggleSymptom(s)}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition ${active ? "border-[#11322A] bg-[#FDFBF7] text-[#0B1317] font-bold" : "border-[#E0E4E2] bg-white text-[#0B1317] hover:border-[#11322A]"}`}
                  data-testid={`care-symptom-${s}`}
                >
                  <span className={`grid h-5 w-5 shrink-0 place-items-center rounded border ${active ? "border-[#11322A] bg-[#11322A] text-white" : "border-[#E0E4E2] bg-white"}`}>
                    {active && <CheckCircle2 className="h-3 w-3" />}
                  </span>
                  {labelSymptom(s, lang)}
                </button>
              );
            })}
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label-cap text-[10px]">{c.body_area}</label>
              <input className="input-field mt-1" placeholder={c.body_area_ph} value={form.body_area} onChange={(e) => set("body_area", e.target.value)} data-testid="care-body-area" />
            </div>
            <div>
              <label className="label-cap text-[10px]">{c.duration}</label>
              <select className="input-field mt-1" value={form.duration} onChange={(e) => set("duration", e.target.value)} data-testid="care-duration">
                <option value="acute">{c.duration_acute}</option>
                <option value="subacute">{c.duration_subacute}</option>
                <option value="chronic">{c.duration_chronic}</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="label-cap text-[10px]">{c.pain_level} · {form.pain_level}/10</label>
              <input type="range" min="1" max="10" value={form.pain_level} onChange={(e) => set("pain_level", parseInt(e.target.value, 10))} className="mt-2 w-full accent-[#E85D04]" data-testid="care-pain" />
              <div className="mt-1 flex justify-between text-[10px] font-bold uppercase tracking-wider text-[#5C6B68]">
                <span>{c.pain_low}</span><span>{c.pain_high}</span>
              </div>
            </div>
          </div>
          <label className="mt-4 flex items-center gap-2 text-sm font-semibold text-[#0B1317]">
            <input type="checkbox" checked={form.accident_related} onChange={(e) => set("accident_related", e.target.checked)} data-testid="care-accident-related" />
            {c.accident_related}
          </label>

          {/* Insurance */}
          <div className="mt-8 label-cap">{c.section_insurance}</div>
          <div className="mt-3">
            <label className="label-cap text-[10px]">{c.insurance_label}</label>
            <select className="input-field mt-1" value={form.insurance_carrier} onChange={(e) => set("insurance_carrier", e.target.value)} required data-testid="care-insurance">
              <option value="">{c.insurance_pick}</option>
              {carriers.map((cc) => <option key={cc} value={cc}>{cc}</option>)}
            </select>
            <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#5C6B68]">
              <Lock className="h-3 w-3" /> {c.insurance_helper}
            </p>
          </div>

          {/* Finish */}
          <div className="mt-8 label-cap">{c.section_finish}</div>
          <div className="mt-3">
            <label className="label-cap text-[10px]">{c.notes_label}</label>
            <textarea rows={3} className="input-field mt-1" placeholder={c.notes_ph} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="care-notes" />
          </div>

          <label className="mt-6 flex items-start gap-3 rounded-lg border border-[#E0E4E2] bg-[#FDFBF7] p-3 text-sm text-[#0B1317]">
            <input type="checkbox" className="mt-0.5" checked={form.consent_given} onChange={(e) => set("consent_given", e.target.checked)} required data-testid="care-consent" />
            <span>{c.consent_label}</span>
          </label>

          <button
            type="submit"
            disabled={submitting || !form.consent_given}
            className="btn-primary mt-6 w-full disabled:opacity-50"
            data-testid="care-submit"
          >
            {submitting ? c.submitting : <>{c.submit} <ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>

        <div className="mt-4 flex items-start gap-2 rounded-lg border border-[#F5B6B0] bg-[#FDE8E6] p-3 text-xs text-[#B91C1C]" data-testid="care-emergency">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>
            {lang === "es"
              ? "Si tienes una emergencia médica, llama al 911 inmediatamente. Este formulario no es para atención de emergencia."
              : "If you're having a medical emergency, call 911 immediately. This form is not for emergency care."}
          </span>
        </div>
      </section>
      <Footer />
    </div>
  );
}
